// The pages a signed-in person sees before they are a verified partner (MOTHER-86): the
// Apply form when they have no partner record, and one plain page for each closed state
// (pending, declined, deactivated). Props only, so tests render each with fake data;
// PartnersPage.tsx picks which one from /api/partner/me.
import { AuthLayout, Button, Field, FormGrid, FormGridItem, InlineMessage, Input, OptionSelect, Stack, Text, Textarea } from '@olytic/foundation';
import { type FormEvent, useState } from 'react';

import type { PartnerMe } from './api';
import type { ApplicationState } from './flow';
import { emptyRegistration, HEARD_ABOUT_OPTIONS, REGISTRATION_FIELDS, type RegistrationKey, type RegistrationValues } from './registration';

const RULE = Object.fromEntries(REGISTRATION_FIELDS.map((rule) => [rule.key, rule])) as Record<RegistrationKey, (typeof REGISTRATION_FIELDS)[number]>;
const HEARD_ABOUT = HEARD_ABOUT_OPTIONS.map((option) => ({ value: option, label: option }));

/** Sign Out, and the message when it failed (the session is still live, so it retries). */
function SignOut({ busy, error, onSignOut }: { busy: boolean; error: string | undefined; onSignOut: () => void }): JSX.Element {
  return (
    <Stack gap="md">
      {error ? (
        <InlineMessage tone="error" role="alert">
          {error}
        </InlineMessage>
      ) : null}
      <Button variant="neutral" onClick={onSignOut} loading={busy}>
        Sign Out
      </Button>
    </Stack>
  );
}

export interface ApplyPageProps {
  me: PartnerMe;
  /** After a refused submit: the values typed, and why. */
  application: ApplicationState | undefined;
  busy: boolean;
  /** A failed Sign Out. */
  error: string | undefined;
  onApply(values: RegistrationValues): void;
  onSignOut(): void;
}

/**
 * "Become a Partner": the seven REGISTRATION_RULES fields, the signed-in email shown
 * read-only above them (never sent). No terms text or checkbox (Cameron, 2026-10-26).
 */
export function ApplyPage({ me, application, busy, error, onApply, onSignOut }: ApplyPageProps): JSX.Element {
  const [values, setValues] = useState<RegistrationValues>(() => application?.values ?? emptyRegistration());
  const fieldErrors = application?.fieldErrors ?? {};
  const set = (key: RegistrationKey) => (value: string) => setValues((current) => ({ ...current, [key]: value }));
  const field = (key: RegistrationKey) => ({ label: RULE[key].label, required: RULE[key].required, error: fieldErrors[key] });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onApply(values);
  };

  return (
    <AuthLayout title="Become a Partner" description="Tell Olytic about you and your company. Olytic reviews every application.">
      <Stack gap="lg">
        <Stack as="form" gap="md" noValidate onSubmit={submit}>
          <Text tone="muted" size="sm">
            Signed in as {me.email}
          </Text>
          {application?.error ? (
            <InlineMessage tone="error" role="alert">
              {application.error}
            </InlineMessage>
          ) : null}
          <FormGrid columns={2}>
            <Field {...field('firstName')}>
              <Input name="firstName" autoComplete="given-name" value={values.firstName} onChange={(event) => set('firstName')(event.target.value)} />
            </Field>
            <Field {...field('lastName')}>
              <Input name="lastName" autoComplete="family-name" value={values.lastName} onChange={(event) => set('lastName')(event.target.value)} />
            </Field>
            <FormGridItem span="full">
              <Field {...field('companyName')}>
                <Input name="companyName" autoComplete="organization" value={values.companyName} onChange={(event) => set('companyName')(event.target.value)} />
              </Field>
            </FormGridItem>
            <Field {...field('companyWebsite')}>
              <Input name="companyWebsite" type="url" autoComplete="url" value={values.companyWebsite} onChange={(event) => set('companyWebsite')(event.target.value)} />
            </Field>
            <Field {...field('phone')}>
              <Input name="phone" type="tel" autoComplete="tel" value={values.phone} onChange={(event) => set('phone')(event.target.value)} />
            </Field>
            <FormGridItem span="full">
              <Field {...field('heardAbout')}>
                <OptionSelect name="heardAbout" options={HEARD_ABOUT} placeholder="Choose one" value={values.heardAbout} onValueChange={set('heardAbout')} />
              </Field>
            </FormGridItem>
            <FormGridItem span="full">
              <Field {...field('partnershipVision')}>
                <Textarea name="partnershipVision" rows={5} value={values.partnershipVision} onChange={(event) => set('partnershipVision')(event.target.value)} />
              </Field>
            </FormGridItem>
          </FormGrid>
          <Button type="submit" variant="brand" loading={busy}>
            {application?.retry ? 'Try Again' : 'Submit Application'}
          </Button>
        </Stack>
        <SignOut busy={false} error={error} onSignOut={onSignOut} />
      </Stack>
    </AuthLayout>
  );
}

export interface ClosedPageProps {
  me: PartnerMe;
  busy: boolean;
  /** A failed Sign Out. */
  error: string | undefined;
  onSignOut(): void;
}

/** Who applied and for which company, from the record the API returned. */
function applicant(me: PartnerMe): { name: string; company: string | null } {
  const partner = me.partner;
  const name = [partner?.firstName, partner?.lastName].filter((part): part is string => typeof part === 'string' && part.trim() !== '').join(' ');
  return { name: name || me.name || me.email, company: partner?.companyName ?? null };
}

/**
 * Pending: Olytic has the application. Names the person and company; promises no
 * date and no message. No form and no link but Sign Out.
 */
export function PendingPage({ me, busy, error, onSignOut }: ClosedPageProps): JSX.Element {
  const { name, company } = applicant(me);
  return (
    <AuthLayout title="We're Reviewing Your Application">
      <Stack gap="lg">
        <Text>
          Thank you, {name}. Olytic has your application{company ? ` for ${company}` : ''} and will decide on it soon.
        </Text>
        <SignOut busy={busy} error={error} onSignOut={onSignOut} />
      </Stack>
    </AuthLayout>
  );
}

/** Declined: no reason and no way to apply again. */
export function DeclinedPage({ busy, error, onSignOut }: ClosedPageProps): JSX.Element {
  return (
    <AuthLayout title="This Application Was Not Approved.">
      <SignOut busy={busy} error={error} onSignOut={onSignOut} />
    </AuthLayout>
  );
}

/** Deactivated: the partner account is closed. */
export function DeactivatedPage({ busy, error, onSignOut }: ClosedPageProps): JSX.Element {
  return (
    <AuthLayout title="This Account Is No Longer Active.">
      <SignOut busy={busy} error={error} onSignOut={onSignOut} />
    </AuthLayout>
  );
}
